"use server";

import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  removeAuthToken,
  removeCartId,
  setAuthToken,
} from "@/lib/data/cookies";
import { bounceOnExpiredSession } from "@/lib/data/session";
import { sdk } from "@/lib/medusa";
import { flag } from "@/lib/util/flag";
import { FetchError } from "@medusajs/js-sdk";
import { HttpTypes } from "@medusajs/types";
import { revalidateTag, updateTag } from "next/cache";

export async function changeCustomerPasswordByToken(
  email: string,
  token: string,
  password: string
) {
  try {
    await sdk.auth.updateProvider(
      "customer",
      "emailpass",
      { email, password },
      token
    );
    return flag(true);
  } catch (e) {
    console.error(e);
    return flag(false, "El enlace es inválido o ha expirado.");
  }
}

export async function resetCustomerPassword(email: string) {
  try {
    await sdk.auth.resetPassword("customer", "emailpass", {
      identifier: email,
    });
    return flag(true);
  } catch (e) {
    console.error(e);
    return flag(false);
  }
}

export const retrieveCustomer =
  async (): Promise<HttpTypes.StoreCustomer | null> => {
    const authHeaders = await getAuthHeaders();
    if (!authHeaders) return null;

    const next = await getCacheOptions("customers");

    return await sdk.client
      .fetch<{ customer: HttpTypes.StoreCustomer }>(`/store/customers/me`, {
        method: "GET",
        query: {
          fields: "*orders",
        },
        headers: authHeaders,
        next,
        cache: "force-cache",
      })
      .then(({ customer }) => customer)
      .catch(() => null);
  };

export async function signUpCustomer(formData: FormData) {
  const password = formData.get("password") as string;
  const customerForm = {
    email: formData.get("email") as string,
    first_name: formData.get("first_name") as string,
    last_name: formData.get("last_name") as string,
    phone: formData.get("phone") as string,
  };

  try {
    const token = await sdk.auth.register("customer", "emailpass", {
      email: customerForm.email,
      password: password,
    });

    const { customer: createdCustomer } = await sdk.store.customer.create(
      customerForm,
      undefined,
      { authorization: `bearer ${token}` }
    );

    const loginToken = await sdk.auth.login("customer", "emailpass", {
      email: customerForm.email,
      password,
    });

    await setAuthToken(loginToken as string);

    const customerCacheTag = await getCacheTag("customers");
    updateTag(customerCacheTag);

    await transferCart();

    return { success: true, customer: createdCustomer };
  } catch (e) {
    const error = e as Error;
    console.error("Signup error:", error);
    return { success: false, error: error?.message ?? String(error) };
  }
}

export async function signInCustomer(
  _currentState: unknown,
  formData: FormData
) {
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;

  try {
    const token = (await sdk.auth.login("customer", "emailpass", {
      email,
      password,
    })) as string;

    await setAuthToken(token);

    const customerCacheTag = await getCacheTag("customers");
    updateTag(customerCacheTag);

    try {
      await transferCart();
    } catch (cartError) {
      console.error("Error transferring cart:", cartError);
    }

    return {
      success: true,
      message: "Login exitoso",
    };
  } catch (e) {
    const error = e as FetchError;
    console.error("Login error:", error);

    let errorMessage = "Error durante el login";
    if (error?.message?.includes("invalid") || error?.status === 401) {
      errorMessage = "Email o contraseña incorrectos";
    } else if (error?.status === 400) {
      errorMessage = "Datos de login inválidos";
    } else if ((error?.status ?? 0) >= 500) {
      errorMessage = "Error del servidor, intenta más tarde";
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

export async function signOutCustomer() {
  await sdk.auth.logout();
  await removeAuthToken();
  await removeCartId();

  const customerCacheTag = await getCacheTag("customers");
  revalidateTag(customerCacheTag, "max");
}

export async function transferCart() {
  const cartId = await getCartId();

  if (!cartId) {
    return;
  }

  const headers = await getAuthHeaders();

  await sdk.store.cart.transferCart(cartId, {}, headers);
}

export async function changeCustomerPasswordByOldPassword(
  oldPassword: string,
  newPassword: string
) {
  try {
    const headers = { ...(await getAuthHeaders()) };

    await sdk.client.fetch(`/store/auth/customer/change-password`, {
      method: "POST",
      headers,
      body: {
        old_password: oldPassword,
        new_password: newPassword,
      },
    });

    const customerCacheTag = await getCacheTag("customers");
    updateTag(customerCacheTag);

    return flag(true, "Contraseña actualizada exitosamente");
  } catch (e) {
    const error = e as FetchError;
    console.error("Error updating password:", error);

    let errorMessage = "Error al actualizar la contraseña";

    if (error?.status === 401) {
      errorMessage = "Contraseña actual incorrecta";
    } else if (error?.status === 400) {
      errorMessage = error?.message || "Datos inválidos";
    }

    return flag(false, errorMessage);
  }
}

export const updateCustomer = async (body: HttpTypes.StoreUpdateCustomer) => {
  try {
    const headers = { ...(await getAuthHeaders()) };
    if (!headers) return flag(false, "No autorizado");

    await sdk.store.customer.update(body, {}, headers);

    const customerCacheTag = await getCacheTag("customers");
    updateTag(customerCacheTag);

    return flag(true);
  } catch (e) {
    await bounceOnExpiredSession(e);
    const error = e as FetchError;
    console.error("Error updating customer profile:", error);

    let errorMessage = "Error al actualizar el perfil";

    if (error?.status === 401) {
      errorMessage = "No autorizado para actualizar la información";
    } else if (error?.status === 400) {
      errorMessage = "Datos inválidos";
    }

    return flag(false, errorMessage);
  }
};
